import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { approvalHistory, approvalTemplates } from "../../../mocks/approvals";
import { supabase } from "../../../lib/supabase";
import { useAuth } from "../../../contexts/AuthContext";
import { useCompany } from "../../../contexts/CompanyContext";

interface Props {
  selectedId: string | null;
  onSelect: (id: string) => void;
  onNew: (templateId: string) => void;
  resubmittedIds?: Set<string>;
}

type StatusFilter = "all" | "pending" | "rejected" | "approved";

const statusCfg: Record<string, { label: string; dot: string; badge: string }> = {
  approved: { label: "승인", dot: "bg-emerald-500", badge: "text-emerald-400/80 bg-emerald-400/10 border-emerald-400/20" },
  rejected: { label: "반려", dot: "bg-red-500", badge: "text-red-400/80 bg-red-400/10 border-red-400/20" },
  pending:  { label: "검토중", dot: "bg-amber-400", badge: "text-amber-400/80 bg-amber-400/10 border-amber-400/20" },
};

const filterTabs: { id: StatusFilter; label: string }[] = [
  { id: "all", label: "전체" },
  { id: "pending", label: "검토중" },
  { id: "rejected", label: "반려" },
  { id: "approved", label: "승인" },
];

const typeGroups = [
  { label: "계약", templates: ["tmpl1", "tmpl8"] },
  { label: "예산·기획", templates: ["tmpl2", "tmpl5", "tmpl6", "tmpl9"] },
  { label: "발매·사업", templates: ["tmpl3", "tmpl7"] },
  { label: "마케팅", templates: ["tmpl10"] },
  { label: "법무", templates: ["tmpl4"] },
];

interface DbDoc { id: string; title: string; doc_type: string; status: string; admin_comment: string | null; submitted_at: string; }

const DB_STATUS_CFG: Record<string, { label: string; dot: string; badge: string }> = {
  pending:  { label: "검토 대기", dot: "bg-amber-400", badge: "text-amber-400/80 bg-amber-400/10 border-amber-400/20" },
  approved: { label: "승인 완료", dot: "bg-emerald-500", badge: "text-emerald-400/80 bg-emerald-400/10 border-emerald-400/20" },
  rejected: { label: "반려",      dot: "bg-red-500",     badge: "text-red-400/80 bg-red-400/10 border-red-400/20" },
};

export default function ApprovalList({ selectedId, onSelect, onNew, resubmittedIds }: Props) {
  const navigate = useNavigate();
  const [tab, setTab] = useState<"new" | "history" | "instructor">("new");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [dbDocs, setDbDocs] = useState<DbDoc[]>([]);
  const [dbLoading, setDbLoading] = useState(false);
  const { user } = useAuth();
  const { workspaceId } = useCompany();

  useEffect(() => {
    if (tab !== "instructor" || !user || !workspaceId) return;
    setDbLoading(true);
    supabase.from("approval_docs").select("id,title,doc_type,status,admin_comment,submitted_at")
      .eq("workspace_id", workspaceId).eq("user_id", user.id).order("submitted_at", { ascending: false })
      .then(({ data }) => { setDbDocs((data ?? []) as DbDoc[]); setDbLoading(false); });
  }, [tab, user, workspaceId]);

  const newInstructorCount = dbDocs.filter(d => d.status !== "pending").length;

  const pending = approvalHistory.filter(h => h.status === "pending").length;
  const rejected = approvalHistory.filter(h => h.status === "rejected").length;
  const filtered = approvalHistory.filter(h => statusFilter === "all" || h.status === statusFilter);

  return (
    <div className="flex flex-col h-full bg-[#111112]">
      {/* 상단 탭 */}
      <div className="flex border-b border-white/[0.05] px-3 pt-3 gap-1">
        <button type="button" onClick={() => setTab("new")}
          className={`flex-1 px-2 py-2 rounded-t-lg text-xs font-medium cursor-pointer transition-colors whitespace-nowrap ${tab === "new" ? "bg-white/[0.05] text-[#CCCCCC]" : "text-[#444444] hover:text-[#888888]"}`}>
          + 새 기안
        </button>
        <button type="button" onClick={() => setTab("history")}
          className={`flex-1 px-2 py-2 rounded-t-lg text-xs font-medium cursor-pointer transition-colors whitespace-nowrap flex items-center justify-center gap-1 ${tab === "history" ? "bg-white/[0.05] text-[#CCCCCC]" : "text-[#444444] hover:text-[#888888]"}`}>
          결재 이력
          {(pending > 0 || rejected > 0) && (
            <span className={`text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center font-bold shrink-0 ${rejected > 0 ? "bg-red-500" : "bg-amber-400"}`}>
              {rejected > 0 ? rejected : pending}
            </span>
          )}
        </button>
        <button type="button" onClick={() => setTab("instructor")}
          className={`flex-1 px-2 py-2 rounded-t-lg text-xs font-medium cursor-pointer transition-colors whitespace-nowrap flex items-center justify-center gap-1 ${tab === "instructor" ? "bg-white/[0.05] text-[#CCCCCC]" : "text-[#444444] hover:text-[#888888]"}`}>
          강사 결재
          {newInstructorCount > 0 && (
            <span className="text-white text-[9px] w-4 h-4 rounded-full flex items-center justify-center font-bold shrink-0 bg-[#4B6CF7]">{newInstructorCount}</span>
          )}
        </button>
      </div>

      {/* 반려 알림 배너 */}
      {tab === "history" && rejected > 0 && (
        <div className="mx-3 mt-2.5 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 flex items-center gap-2">
          <i className="ri-error-warning-fill text-red-400 text-sm shrink-0"></i>
          <div className="flex-1 min-w-0">
            <p className="text-red-400/90 text-[11px] font-semibold">반려된 문서 {rejected}건</p>
            <p className="text-red-400/60 text-[10px]">수정 후 재제출이 필요합니다</p>
          </div>
          <button type="button" onClick={() => setStatusFilter("rejected")}
            className="text-[10px] text-red-400 border border-red-400/30 px-2 py-0.5 rounded cursor-pointer hover:bg-red-400/10 transition-colors whitespace-nowrap">
            보기
          </button>
        </div>
      )}

      {/* 히스토리 필터 */}
      {tab === "history" && (
        <div className="flex gap-1 px-3 py-2">
          {filterTabs.map(f => (
            <button key={f.id} type="button" onClick={() => setStatusFilter(f.id)}
              className={`px-2.5 py-1 rounded-full text-[10px] font-medium cursor-pointer transition-all whitespace-nowrap ${
                statusFilter === f.id
                  ? f.id === "rejected" ? "bg-red-500/20 text-red-400 border border-red-400/30"
                    : f.id === "pending" ? "bg-amber-400/20 text-amber-400 border border-amber-400/30"
                    : f.id === "approved" ? "bg-emerald-400/20 text-emerald-400 border border-emerald-400/30"
                    : "bg-white/[0.08] text-[#CCCCCC] border border-white/[0.1]"
                  : "text-[#444444] hover:text-[#888888] border border-transparent"
              }`}>
              {f.label}
              {f.id === "rejected" && rejected > 0 && <span className="ml-1 text-red-400/70">{rejected}</span>}
              {f.id === "pending" && pending > 0 && <span className="ml-1 text-amber-400/70">{pending}</span>}
            </button>
          ))}
        </div>
      )}

      <div className="flex-1 overflow-y-auto">
        {tab === "instructor" ? (
          <div>
            {dbLoading ? <div className="text-center py-8 text-[#333333] text-xs">로딩 중...</div> :
            dbDocs.length === 0 ? <div className="text-center py-10 text-[#333333] text-xs">제출한 결재 문서가 없습니다</div> :
            dbDocs.map(doc => {
              const cfg = DB_STATUS_CFG[doc.status] ?? DB_STATUS_CFG.pending;
              return (
                <div key={doc.id} className="px-3 py-3 border-b border-white/[0.03]">
                  <div className="flex items-start gap-2.5">
                    <div className={`w-1.5 h-1.5 rounded-full shrink-0 mt-1.5 ${cfg.dot}`}></div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-0.5 gap-1">
                        <span className="text-xs font-medium truncate text-[#AAAAAA]">{doc.title}</span>
                        <span className={`text-[9px] px-1.5 py-0.5 rounded border shrink-0 ${cfg.badge}`}>{cfg.label}</span>
                      </div>
                      <p className="text-[10px] text-[#2A2A2A]">{new Date(doc.submitted_at).toLocaleDateString("ko-KR")}</p>
                      {doc.admin_comment && (
                        <p className="text-[10px] text-[#555555] mt-1 bg-white/[0.03] rounded px-2 py-1 leading-relaxed">💬 {doc.admin_comment}</p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        ) : tab === "new" ? (
          <div className="px-3 py-3 space-y-4">
            {typeGroups.map(group => {
              const templates = approvalTemplates.filter(t => group.templates.includes(t.id));
              if (!templates.length) return null;
              return (
                <div key={group.label}>
                  <p className="text-[#2A2A2A] text-[9px] uppercase tracking-widest mb-1.5 px-1">{group.label}</p>
                  <div className="space-y-0.5">
                    {templates.map(tmpl => (
                      <button key={tmpl.id} type="button" onClick={() => onNew(tmpl.id)}
                        className="w-full flex items-center gap-2.5 px-2.5 py-2.5 rounded-lg text-[#555555] hover:text-[#CCCCCC] hover:bg-white/[0.04] transition-all cursor-pointer text-left group">
                        <div className="w-7 h-7 flex items-center justify-center rounded-lg bg-white/[0.04] group-hover:bg-white/[0.07] shrink-0 transition-colors">
                          <i className={`${tmpl.icon} text-sm text-[#4B6CF7]/60 group-hover:text-[#4B6CF7]/90`}></i>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-xs font-medium truncate">{tmpl.title}</p>
                          <p className="text-[9px] text-[#333333] truncate">{tmpl.description}</p>
                        </div>
                        <i className="ri-pencil-line text-[#2A2A2A] text-xs group-hover:text-[#555555] transition-colors"></i>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div>
            {filtered.length === 0 && (
              <div className="text-center py-10 text-[#333333] text-xs">해당 상태의 문서가 없습니다</div>
            )}
            {filtered.map(ap => {
              const cfg = statusCfg[ap.status];
              const isSelected = selectedId === ap.id;
              const isRejected = ap.status === "rejected";
              const isResubmitted = resubmittedIds?.has(ap.id) ?? false;
              return (
                <div key={ap.id} onClick={() => onSelect(ap.id)}
                  className={`px-3 py-3 border-b border-white/[0.03] cursor-pointer transition-all ${
                    isSelected
                      ? isRejected ? "bg-red-500/[0.07] border-l-2 border-l-red-500/60 pl-[10px]"
                        : "bg-white/[0.05] border-l-2 border-l-[#4B6CF7]/70 pl-[10px]"
                      : isRejected && !isResubmitted
                        ? "border-l-2 border-l-red-500/30 pl-[10px] hover:bg-red-500/[0.04]"
                        : "hover:bg-white/[0.02]"
                  }`}>
                  <div className="flex items-start gap-2.5">
                    <div className={`w-1.5 h-1.5 rounded-full shrink-0 mt-1.5 ${cfg?.dot ?? "bg-white/20"}`}></div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-0.5 gap-1 flex-wrap">
                        <span className={`text-xs font-medium truncate ${isSelected ? "text-[#F2F2F2]" : isRejected && !isResubmitted ? "text-red-300/80" : "text-[#AAAAAA]"}`}>
                          {ap.title}
                        </span>
                        <div className="flex items-center gap-1 shrink-0">
                          {isResubmitted && (
                            <span className="text-[9px] px-1.5 py-0.5 rounded border text-emerald-400/80 bg-emerald-400/10 border-emerald-400/20 font-semibold flex items-center gap-0.5 whitespace-nowrap">
                              <i className="ri-refresh-line text-[9px]"></i>재기안 완료
                            </span>
                          )}
                          <span className={`text-[9px] px-1.5 py-0.5 rounded border ${cfg?.badge}`}>{cfg?.label}</span>
                        </div>
                      </div>
                      <p className="text-[10px] text-[#3D3D3D]">{ap.submittedBy}</p>
                      <p className="text-[10px] text-[#2A2A2A]">{ap.submittedAt}</p>
                      {isRejected && !isResubmitted && (
                        <button type="button"
                          onClick={e => { e.stopPropagation(); onSelect(ap.id); navigate("/approval"); }}
                          className="mt-1.5 flex items-center gap-1 text-[10px] text-red-400/80 hover:text-red-400 transition-colors cursor-pointer">
                          <i className="ri-refresh-line text-[10px]"></i>수정 후 재제출
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
